/**
 * A document shared with a Customer Portal contact, opened in Docs' own editor -- read-only, because what a customer is
 * shared is the RELEASED revision, and Docs never edits a released revision.
 *
 * REACHED FROM THE PORTAL ONLY, as /shared/<share>#access=<token>. The token is minted by Docs for that one share and
 * may reach one endpoint (cres_t11/service/customer_access.py). It is read from the fragment once, kept in this tab's
 * sessionStorage under the share, and taken out of the address bar at once, so a copied link carries no access.
 *
 * OUTSIDE THE STAFF LAYOUT, deliberately: the side rail, the document list and the account menu all assume a staff
 * session this visitor does not have, and every one of them would be a door a customer token cannot open.
 */
import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {Box, CircularProgress, Typography} from "@mui/material";
import DocumentEditor from "../components/Docs/DocumentEditor";
import {Grey5, Grey17} from "../theme/styles";
import {tokenFromHash} from "../util/sharedLink";

const KEY = (share: string) => `customerAccess:${share}`;

function takeToken(share: string): string | null {
    const fromLink = tokenFromHash(window.location.hash);
    try {
        if (fromLink) {
            window.sessionStorage.setItem(KEY(share), fromLink);
            window.history.replaceState({}, "", window.location.pathname);
            return fromLink;
        }
        return window.sessionStorage.getItem(KEY(share));
    } catch {
        return fromLink;
    }
}

export default function SharedDocumentPage({share}: {share: string}) {
    const [token] = useState<string | null>(() => takeToken(share));
    const [content, setContent] = useState<any>(null);
    const [problem, setProblem] = useState<string | null>(null);

    const read = useMemo(() => async () => {
        const answer = await axios.get(`${process.env.REACT_APP_API_URL}/t11/v1/shared_documents`,
                                       {params: {USID: share}, headers: {"X-Cres-AccessToken": token}});
        return answer.data;
    }, [share, token]);

    useEffect(() => {
        if (!token) {
            setProblem("This link has no access with it. Open the document again from the Customer Portal.");
            return;
        }
        read().then(setContent).catch((failure: any) => {
            const status = failure?.response?.status;
            setProblem(status === 401 || status === 403
                ? "This link has expired. Open the document again from the Customer Portal."
                : failure?.response?.data?.Cause ?? "This document could not be opened.");
        });
    }, [read, token]);

    // WHAT THE EDITOR READS OF A SESSION, for a visitor who has none: a name for the page furniture, and the
    // manufacturer's name for the confidentiality marking. No realm, so no collaboration channel is opened.
    const visitor = useMemo(() => ({getName: () => "You", getAccountName: () => content?.From ?? null}), [content?.From]);

    const api = useMemo(() => ({
        get: async () => read(),
        save: async () => { throw new Error("This document is shared with you to read."); },
    }), [read]);

    if (problem) {
        return <Box sx={{p: 6, maxWidth: 640, mx: "auto"}}>
            <Typography sx={{fontSize: 20, fontWeight: 500, color: Grey17, mb: 1}}>ParkourAI Docs</Typography>
            <Typography sx={{fontSize: 14, color: Grey17, lineHeight: 1.6}}>{problem}</Typography>
        </Box>;
    }
    if (!content) {
        return <Box sx={{display: "flex", justifyContent: "center", p: 8}}><CircularProgress/></Box>;
    }

    const record = content.Record ?? {};
    return (
        <Box sx={{px: 3, py: 2}}>
            <Box sx={{mb: 1.5}}>
                <Typography sx={{fontSize: 22, fontWeight: 500, color: Grey17}}>
                    {record.ObjectID ? `${record.ObjectID} · ` : ""}{record.Name ?? content.Name}
                </Typography>
                <Typography sx={{fontSize: 12, color: Grey5}}>
                    {content.From ? `From ${content.From} · ` : ""}
                    {record.DisplayRevision ? `Rev ${record.DisplayRevision} · ` : ""}released · shared with you to read
                </Typography>
            </Box>
            <DocumentEditor documentUSID={content.USID} revisionUSID={content.RevisionUSID}
                            mqttClient={null} sessionContext={visitor} api={api as any}
                            record={record} status={content.Status}/>
        </Box>
    );
}
